// utils/idFormatValidation.ts
export interface IDFormatRule {
  pattern: RegExp;
  format: string;
  example: string;
  description: string;
}

export const ID_FORMAT_RULES: Record<string, IDFormatRule> = {
  'Passport': {
    pattern: /^[A-Z][0-9]{7}[A-Z]?$/,
    format: 'A1234567A',
    example: 'P1234567A',
    description: 'Letter followed by 7 digits, optionally ending with a letter'
  },
  'Passport Number': {
    pattern: /^[A-Z][0-9]{7}[A-Z]?$/,
    format: 'A1234567A',
    example: 'P1234567A',
    description: 'Letter followed by 7 digits, optionally ending with a letter'
  },
  'National ID': {
    pattern: /^\d{4}-\d{4}-\d{4}$/,
    format: '1234-5678-9123',
    example: '1234-5678-9123',
    description: '12 digits separated by hyphens (XXXX-XXXX-XXXX)'
  },
  'Driver License': {
    pattern: /^[A-Z0-9]{1,3}-\d{2}-\d{6,7}$/,
    format: 'N01-23-456789',
    example: 'N01-23-456789',
    description: '1-3 alphanumeric characters, dash, 2 digits, dash, 6-7 digits'
  },
  'Driver\'s License': {
    pattern: /^[A-Z0-9]{1,3}-\d{2}-\d{6,7}$/,
    format: 'N01-23-456789',
    example: 'N01-23-456789',
    description: '1-3 alphanumeric characters, dash, 2 digits, dash, 6-7 digits'
  },
  'Unified Multi-purpose ID': {
    pattern: /^\d{4}-\d{5}-\d{1}$/,
    format: '1234-56789-1',
    example: '1234-56789-1',
    description: '4 digits, dash, 5 digits, dash, 1 digit'
  },
  'UMID': {
    pattern: /^\d{4}-\d{5}-\d{1}$/,
    format: '1234-56789-1',
    example: '1234-56789-1',
    description: '4 digits, dash, 5 digits, dash, 1 digit'
  },
  'Social Security Number': {
    pattern: /^\d{2}-\d{7}-\d{1}$/,
    format: '34-1234567-8',
    example: '34-1234567-8',
    description: '2 digits, dash, 7 digits, dash, 1 digit'
  },
  'SSS': {
    pattern: /^\d{2}-\d{7}-\d{1}$/,
    format: '34-1234567-8',
    example: '34-1234567-8',
    description: '2 digits, dash, 7 digits, dash, 1 digit'
  },
  'PhilHealth ID': {
    pattern: /^\d{2}-\d{9}-\d{1}$/,
    format: '12-345678901-2',
    example: '12-345678901-2',
    description: '2 digits, dash, 9 digits, dash, 1 digit'
  },
  'PhilHealth': {
    pattern: /^\d{2}-\d{9}-\d{1}$/,
    format: '12-345678901-2',
    example: '12-345678901-2',
    description: '2 digits, dash, 9 digits, dash, 1 digit'
  },
  'TIN': {
    pattern: /^\d{3}-\d{3}-\d{3}(-\d{3})?$/,
    format: '123-456-789 or 123-456-789-123',
    example: '123-456-789-123',
    description: '9 digits in XXX-XXX-XXX format, optionally with 3 more digits for branch'
  },
  'Tax Identification Number': {
    pattern: /^\d{3}-\d{3}-\d{3}(-\d{3})?$/,
    format: '123-456-789 or 123-456-789-123',
    example: '123-456-789-123',
    description: '9 digits in XXX-XXX-XXX format, optionally with 3 more digits for branch'
  },
  'Postal ID': {
    pattern: /^\d{4}-\d{4}-\d{4}-\d{4}$/,
    format: '1234-5678-9012-3456',
    example: '1234-5678-9012-3456',
    description: '16 digits separated by hyphens (XXXX-XXXX-XXXX-XXXX)'
  },
  'Postal ID Number': {
    pattern: /^\d{4}-\d{4}-\d{4}-\d{4}$/,
    format: '1234-5678-9012-3456',
    example: '1234-5678-9012-3456',
    description: '16 digits separated by hyphens (XXXX-XXXX-XXXX-XXXX)'
  },
  'PRC License': {
    pattern: /^\d{7}$/,
    format: '1234567',
    example: '1234567',
    description: '7 digits'
  },
  'PRC Number': {
    pattern: /^\d{7}$/,
    format: '1234567',
    example: '1234567',
    description: '7 digits'
  },
  'Professional License': {
    pattern: /^\d{7}$/,
    format: '1234567',
    example: '1234567',
    description: '7 digits'
  }
};

export interface ValidationResult {
  isValid: boolean;
  message?: string;
}

export const validateIDNumber = (idType: string, idNumber: string): ValidationResult => {
  // Remove leading/trailing whitespace
  const trimmedIdNumber = idNumber.trim();
  
  // Check if ID number is empty
  if (!trimmedIdNumber) {
    return {
      isValid: false,
      message: 'ID number is required'
    };
  }

  // Find the format rule for the ID type
  const formatRule = ID_FORMAT_RULES[idType];
  
  if (!formatRule) {
    // If no specific format rule exists, just check if it's not empty
    return {
      isValid: true
    };
  }

  // Test against the pattern
  const isValid = formatRule.pattern.test(trimmedIdNumber);
  
  return {
    isValid,
    message: isValid ? undefined : `Invalid format. Expected format: ${formatRule.format} (e.g., ${formatRule.example})`
  };
};

export const getIDFormatInfo = (idType: string): IDFormatRule | null => {
  return ID_FORMAT_RULES[idType] || null;
};

export const formatIDNumber = (idType: string, value: string): string => {
  const formatRule = ID_FORMAT_RULES[idType];
  if (!formatRule) return value;

  // Remove all non-alphanumeric characters first
  const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  
  switch (idType) {
    case 'National ID':
      // Format: XXXX-XXXX-XXXX
      if (cleaned.length <= 4) return cleaned;
      if (cleaned.length <= 8) return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
      return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 8)}-${cleaned.slice(8, 12)}`;
      
    case 'Driver License':
    case 'Driver\'s License':
      // Format: ABC-XX-XXXXXXX
      if (cleaned.length <= 3) return cleaned;
      if (cleaned.length <= 5) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5, 12)}`;
      
    case 'Unified Multi-purpose ID':
    case 'Unified Multi-Purpose ID':        
    case 'UMID':
      // Format: XXXX-XXXXX-X
      if (cleaned.length <= 4) return cleaned;
      if (cleaned.length <= 9) return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
      return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 9)}-${cleaned.slice(9, 10)}`;
      
    case 'Social Security Number':
    case 'SSS':
      // Format: XX-XXXXXXX-X
      if (cleaned.length <= 2) return cleaned;
      if (cleaned.length <= 9) return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`;
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 9)}-${cleaned.slice(9, 10)}`;
      
    case 'PhilHealth ID':
    case 'PhilHealth':
      // Format: XX-XXXXXXXXX-X
      if (cleaned.length <= 2) return cleaned;
      if (cleaned.length <= 11) return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`;
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 11)}-${cleaned.slice(11, 12)}`;
      
    case 'TIN':
    case 'Tax Identification Number':
      // Format: XXX-XXX-XXX or XXX-XXX-XXX-XXX
      if (cleaned.length <= 3) return cleaned;
      if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
      if (cleaned.length <= 9) return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 9)}-${cleaned.slice(9, 12)}`;
      
    case 'Postal ID':
    case 'Postal ID Number':
      // Format: XXXX-XXXX-XXXX-XXXX
      if (cleaned.length <= 4) return cleaned;
      if (cleaned.length <= 8) return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
      if (cleaned.length <= 12) return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 8)}-${cleaned.slice(8)}`;
      return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 8)}-${cleaned.slice(8, 12)}-${cleaned.slice(12, 16)}`;
      
    default:
      return value; // Return original value for types without special formatting
  }
};
